import { useEffect, useRef } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { getConfluenceResultAcceptanceAttributesForAskQuestion } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions/get-confluence-result-acceptance-attributes-for-ask-question';
import {
	encodeAdf,
	encodePromptToAdf,
} from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	createRemixRegenerateCollect,
	type RemixRegenerateSource,
} from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import { useChatContextStoreActions } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import { useChatMessagesActions } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';
import {
	useMessageIdSelectedForPreview,
	useStagingAreaState,
} from '@atlassian/conversation-assistant-store/controllers/staging-area';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';
import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import { AifcContentCopiedCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/aifc-content-copied-command';
import { AifcExperienceTrackingCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/aifc-preview-experience-tracking-command';
import {
	ContentDestroyCommand,
	ContentReadyCommand,
} from '@atlassian/rovo-content-bridge-api-commands/commands/content-lifecycle-commands';
import { InlineInputBoxAbortMessageCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/inline-input-box-abort-message-command';
import { InlineInputBoxSendMessageCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/inline-input-box-send-message-command';
import {
	SmartCreationModalActivatedCommand,
	SmartCreationModalDeactivatedCommand,
} from '@atlassian/rovo-content-bridge-api-commands/commands/smart-creation-modal-commands';
import { isWebviewContext } from '@atlassian/rovo-content-bridge-api/desktop-apis';
import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';

import { useChatStore } from '../../chat-store/useChatStore';
import { useChatStoreActions } from '../../chat-store/useChatStoreActions';
import { broadcastStreamStatus } from '../streaming-lifecycle/broadcastStreamStatus';
import { StreamLifecycleState } from '../streaming-lifecycle/StreamLifecycleState';

import { usePromptStream } from './use-prompt-stream';

export const BridgeAPIHandlers = () => {
	const [{ currentConversationId }] = useChatStore();
	const currentConversationIdRef = useRef(currentConversationId);
	currentConversationIdRef.current = currentConversationId;
	const { sendMessage, abortRequests } = useChatStoreActions();
	const {
		getTarget,
		getContentContext,
		getCurrentTargetId,
		setTarget,
		clearTarget,
		getActiveContext,
		setCurrentTargetId,
	} = useChatContextStoreActions();
	const promptStreamHook = usePromptStream();
	const experienceTracker = useAIMateExperienceTracker();
	const { trackAIMAUEvent } = useAnalytics();
	const [messageIdSelectedForPreview] = useMessageIdSelectedForPreview();
	const { stagingAreaEntryPoint } = useStagingAreaState();
	const { getMessages } = useChatMessagesActions();
	const { preferences } = useUserPreferences();
	const { storeUserMessages } = preferences;
	const messageIdSelectedForPreviewRef = useRef(messageIdSelectedForPreview);
	messageIdSelectedForPreviewRef.current = messageIdSelectedForPreview;
	const stagingAreaEntryPointRef = useRef(stagingAreaEntryPoint);
	stagingAreaEntryPointRef.current = stagingAreaEntryPoint;
	const trackAIMAUEventRef = useRef(trackAIMAUEvent);
	trackAIMAUEventRef.current = trackAIMAUEvent;

	const bridgeAPI = getBrowserBridgeAPI();

	const isFirstInlineMessageOnGlobalContent = useRef(false);
	const storeUserMessagesRef = useRef(storeUserMessages);
	storeUserMessagesRef.current = storeUserMessages;

	useEffect(() => {
		// we don't want to clash between the rovo chat client which is offered and implemented in desktop
		// and web; in a future iteration, we will re-use the below code in desktop itself.
		if (isWebviewContext()) {
			return;
		}

		bridgeAPI.on(ROVO_CHAT_CLIENT_ID, ContentReadyCommand, (command) => {
			const ctx = command.contentContext;
			setTarget(ctx, {
				id: command.contentId,
				type: command.contentType,
				canProvideContext: command.canProvideContext,
			});

			const currentContext = getActiveContext();
			isFirstInlineMessageOnGlobalContent.current = currentContext === 'global';

			// Mark this content as the active target for staging-area context.
			// This ensures broadcasts and follow-up messages use the correct content
			// even if the modal re-sets the page target.
			if (
				(ctx === 'staging-area' || currentContext === 'staging-area') &&
				expValEquals('cc-maui-experiment', 'isEnabled', true)
			) {
				setCurrentTargetId(command.contentId);
			}
		});

		bridgeAPI.on(ROVO_CHAT_CLIENT_ID, ContentDestroyCommand, (command) => {
			const ctx = command.contentContext;

			// Targets are stored as arrays; clear by specific ID for the target content.
			clearTarget(ctx, command.contentId);

			const currentContext = getActiveContext();
			isFirstInlineMessageOnGlobalContent.current = currentContext === 'global';
		});

		bridgeAPI.on(ROVO_CHAT_CLIENT_ID, InlineInputBoxSendMessageCommand, (command) => {
			if (!command.prompt || !command.contentId) {
				return;
			}

			setCurrentTargetId(command.contentId);

			const encodedPrompt = command.skillMetadata
				? encodePromptToAdf(command.prompt, command.skillMetadata).adf
				: encodeAdf(command.prompt);

			sendMessage({
				...command,
				prompt: encodedPrompt,
				// TODO(ai_mate_store_messages_be_driven): remove this local storeUserMessages payload
				// value when BE user settings are fully rolled out; BE overrides it via the capability header.
				storeUserMessages: storeUserMessagesRef.current,
				promptStreamHook,
				messageSources: {
					interactionSource:
						command.source === 'CONFLUENCE_MAUI_INLINE_EDITOR'
							? 'inlineRovoFromRemix'
							: 'inlineRovo',
					followUpSource:
						command.source === 'CONFLUENCE_MAUI_INLINE_EDITOR'
							? 'inlineRovoFromRemix'
							: 'inlineRovo',
					// Mark the Create with Rovo blank-object route on AIMAU events.
					entryPoint:
						command.source === 'CONFLUENCE_CWR_BLANK_OBJECT' ? 'cwrBlankObject' : undefined,
				},
				// Using agents with inline prompt should always start a new chat
				// The first inline prompt message on a new content ID should always start a new chat
				conversationId:
					command.agentId ||
					command.shouldStartNewConversation ||
					isFirstInlineMessageOnGlobalContent.current
						? EMPTY_CONVERSATION_ID
						: undefined,
			});

			isFirstInlineMessageOnGlobalContent.current = false;
		});

		bridgeAPI.on(ROVO_CHAT_CLIENT_ID, InlineInputBoxAbortMessageCommand, () => {
			abortRequests({
				conversationId: currentConversationIdRef.current,
			});
			if (expValEquals('platform_editor_remove_pause_streaming', 'isEnabled', true)) {
				const currentTargetId = getCurrentTargetId();
				const currentTargetContext = currentTargetId
					? (getContentContext(currentTargetId) ?? getActiveContext())
					: getActiveContext();
				const activeTarget = getTarget(currentTargetContext, currentTargetId);
				const activeTargetType = activeTarget?.type;
				if (activeTarget?.id && activeTargetType === 'page') {
					broadcastStreamStatus(StreamLifecycleState.ABORTED, `inline-abort-${Date.now()}`, {
						id: activeTarget.id,
						type: activeTargetType,
					});
				}
			}
			experienceTracker.abort({
				name: ExperienceName.REMIX_REGENERATE,
				reason: 'User cancelled remix thinking',
			});
		});

		bridgeAPI.on(ROVO_CHAT_CLIENT_ID, AifcExperienceTrackingCommand, (command) => {
			if (!command?.status || !command.experienceName) {
				return;
			}

			const featureArea = command.attributes?.featureArea as string | undefined;

			if (command.status === 'start') {
				const isEditExperienceTrackerFixEnabled = !fg(
					'cc-maui-disable-edit-experience-tracker-fixes',
				);
				const attributes = {
					...(command.attributes || {}),
					...(featureArea !== undefined && { featureArea }),
					...(command.actionType && { actionType: command.actionType }),
					...(command.actionId && { actionId: command.actionId }),
					...(command.contentContext && { contentContext: command.contentContext }),
					...(command.dynamicUiSubtype && { dynamicUiSubtype: command.dynamicUiSubtype }),
					...(command.remixType && { remixType: command.remixType }),
					...(command.remixSubtype && { remixSubtype: command.remixSubtype }),
					...(currentConversationIdRef.current && {
						conversationChannelId: currentConversationIdRef.current,
					}),
				};
				let collect;
				if (isEditExperienceTrackerFixEnabled) {
					// Collect callbacks have to be constructed parent-side because functions are
					// not serializable and cannot be sent over the bridge as command payload data.
					switch (command.experienceName) {
						case ExperienceName.REMIX_REGENERATE: {
							collect = createRemixRegenerateCollect({
								conversationChannelId: currentConversationIdRef.current,
								regenerateSource: command.attributes?.regenerateSource as
									| RemixRegenerateSource
									| undefined,
								featureArea,
								allowCollectFallback: true,
								targetIds: {
									actionId: command.actionId ?? undefined,
									selectionLocalId: command.actionId ?? undefined,
								},
							});
							break;
						}
						default:
							break;
					}
				}
				experienceTracker.start({
					name: command.experienceName,
					id: command.messageId ?? command.actionId ?? command.experienceName,
					attributes,
					...(collect ? { collect } : {}),
				});
			} else if (command.status === 'success') {
				experienceTracker.succeed({
					name: command.experienceName,
					...(command.messageId && { id: command.messageId }),
					attributes: {
						...(command.attributes || {}),
						...(featureArea !== undefined && { featureArea }),
						...(command.actionType && { actionType: command.actionType }),
						...(command.actionId && { actionId: command.actionId }),
						...(command.contentContext && { contentContext: command.contentContext }),
						...(command.dynamicUiSubtype && { dynamicUiSubtype: command.dynamicUiSubtype }),
						...(command.remixType && { remixType: command.remixType }),
						...(command.remixSubtype && { remixSubtype: command.remixSubtype }),
						...(command.isSafetyViolation && { isSafetyViolation: command.isSafetyViolation }),
					},
				});
			} else if (command.status === 'fail') {
				experienceTracker.fail({
					name: command.experienceName,
					...(command.messageId && { id: command.messageId }),
					error: new Error(command.error ?? 'Unknown error'),
					attributes: {
						...(command.attributes || {}),
						...(featureArea !== undefined && { featureArea }),
						...(command.actionType && { actionType: command.actionType }),
						...(command.actionId && { actionId: command.actionId }),
						...(command.contentContext && { contentContext: command.contentContext }),
						...(command.dynamicUiSubtype && { dynamicUiSubtype: command.dynamicUiSubtype }),
						...(command.remixType && { remixType: command.remixType }),
						...(command.remixSubtype && { remixSubtype: command.remixSubtype }),
					},
				});
			} else if (command.status === 'abort') {
				const isKillswitchEnabled = FeatureGates.getExperimentValue(
					'platform_aifc_bridge_experience_tracker_fix_aborts',
					'value',
					false,
				);
				const attributes = {
					...(command.attributes || {}),
					...(featureArea !== undefined && { featureArea }),
					...(command.actionType && { actionType: command.actionType }),
					...(command.actionId && { actionId: command.actionId }),
					...(command.contentContext && { contentContext: command.contentContext }),
					...(command.dynamicUiSubtype && { dynamicUiSubtype: command.dynamicUiSubtype }),
				};
				if (isKillswitchEnabled) {
					experienceTracker.fail({
						name: command.experienceName,
						...(command.messageId && { id: command.messageId }),
						error: new Error(command.reason ?? 'Aborted'),
						attributes,
					});
				} else {
					experienceTracker.abort({
						name: command.experienceName,
						...(command.messageId && { id: command.messageId }),
						reason: command.reason ?? 'Aborted',
						attributes,
					});
				}
			}
		});

		bridgeAPI.on(ROVO_CHAT_CLIENT_ID, SmartCreationModalActivatedCommand, (command) => {
			setTarget('staging-area', {
				id: command.type, // Currently, there is only Jira is using this Command and Jira doesn't have a content id for the smart creation modal
				type: command.type,
				canProvideContext: true,
			});
		});

		bridgeAPI.on(ROVO_CHAT_CLIENT_ID, SmartCreationModalDeactivatedCommand, (command) => {
			if (getTarget('staging-area')?.id === command.type) {
				clearTarget('staging-area', command.type);

				if (expValEquals('cc-maui-experiment', 'isEnabled', true)) {
					setCurrentTargetId(undefined);
				}
			}
		});

		bridgeAPI.on(ROVO_CHAT_CLIENT_ID, AifcContentCopiedCommand, (command) => {
			const askQuestionAttributes = expValEquals('cc-disambiguation-in-cwr', 'isEnabled', true)
				? getConfluenceResultAcceptanceAttributesForAskQuestion({
						messages: getMessages(currentConversationIdRef.current ?? ''),
						contentId: command.contentId,
					})
				: undefined;

			trackAIMAUEventRef.current({
				type: 'actioned',
				actionResult: 'copy',
				source: 'AIMate',
				invokedFrom: 'AIFC',
				proactive: false,
				userGenerated: true,
				singleInstrumentationID: messageIdSelectedForPreviewRef.current ?? undefined,
				attributes: {
					isInSmartCreateFlow: true,
					createdContentProduct: 'confluence',
					createdContentType: command.contentType,
					copiedCharCount: command.copiedCharCount,
					copiedWordCount: command.copiedWordCount,
					totalDocCharCount: command.totalDocCharCount,
					copiedPercentage: command.copiedPercentage,
					entryPoint: stagingAreaEntryPointRef.current || undefined,
					...(askQuestionAttributes ?? {}),
				},
			});
		});

		// handlers have been setup - now we can mark ourselves as 'ready' to handle incoming messages
		bridgeAPI.ready(ROVO_CHAT_CLIENT_ID);
	}, [
		bridgeAPI,
		sendMessage,
		abortRequests,
		promptStreamHook,
		setTarget,
		clearTarget,
		getTarget,
		getContentContext,
		getCurrentTargetId,
		getActiveContext,
		setCurrentTargetId,
		experienceTracker,
		currentConversationId,
		getMessages,
	]);

	return null;
};
